import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, Sparkles, ChevronDown, Check } from "lucide-react";
import { motion } from "framer-motion";
import BrandIcon from "./BrandIcon";

const GRADIENT = "linear-gradient(118deg, #346af8 0%, #588af0 100%)";

export default function RateCalculator({ compact = false }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [search, setSearch] = useState("");
  const [balance, setBalance] = useState(100);
  const [hasReceipt, setHasReceipt] = useState(false);
  const [partial, setPartial] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/brands?limit=200").then((r) => {
      setBrands(r.data);
      if (r.data.length && !brandId) setBrandId(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => brands.find((b) => b.id === brandId),
    [brands, brandId]
  );
  const filtered = useMemo(
    () =>
      brands
        .filter((b) => b.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 80),
    [brands, search]
  );

  const effectiveRate = useMemo(() => {
    if (!selected) return 0;
    const rates = selected.rates || {
      receipt: selected.rate,
      no_receipt: selected.rate,
      partial: selected.rate,
    };
    if (partial) return rates.partial;
    if (hasReceipt) return rates.receipt;
    return rates.no_receipt;
  }, [selected, hasReceipt, partial]);

  const payout = selected
    ? Math.max(0, Math.min(balance, selected.max) * (effectiveRate / 100)).toFixed(2)
    : "0.00";

  const goSubmit = () => {
    if (!selected) return;
    nav(`/submit?brand=${encodeURIComponent(selected.slug)}&balance=${balance}`);
  };

  return (
    <div
      className={`rc-shell ${compact ? "rc-compact" : ""}`}
      data-testid="rate-calculator"
    >
      {/* ambient gradient glows */}
      <span className="rc-glow rc-glow-1" aria-hidden />
      <span className="rc-glow rc-glow-2" aria-hidden />

      <div className="rc-inner">
        {/* header */}
        <div className="rc-head">
          <div>
            <div className="rc-eyebrow">Live estimate</div>
            <h3 className="rc-title">What’s your card worth?</h3>
          </div>
          <span className="rc-spark" style={{ backgroundImage: GRADIENT }}>
            <Sparkles className="w-4 h-4 text-white" />
          </span>
        </div>

        {/* brand select */}
        <div className="rc-field-wrap">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="calc-brand-select"
            className="rc-select"
          >
            {selected ? (
              <>
                <span className="rc-brand-chip" style={{ background: selected.color }}>
                  <BrandIcon logoKey={selected.logo_key} name={selected.name} className="w-full h-full" />
                </span>
                <span className="rc-brand-name">{selected.name}</span>
                <span className="rc-rate-pill" style={{ backgroundImage: GRADIENT }}>
                  {effectiveRate}%
                </span>
              </>
            ) : (
              <span className="rc-placeholder">Select a brand</span>
            )}
            <ChevronDown className={`w-4 h-4 rc-chevron ${open ? "rc-chevron-open" : ""}`} />
          </button>

          {open && (
            <div className="rc-dropdown" data-testid="calc-brand-dropdown">
              <div className="rc-search-wrap">
                <Search className="w-4 h-4 rc-search-icon" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search brands…"
                  data-testid="calc-brand-search"
                  className="rc-search-input"
                  autoFocus
                />
              </div>
              <ul className="rc-options">
                {filtered.map((b) => (
                  <li key={b.id}>
                    <button
                      onClick={() => {
                        setBrandId(b.id);
                        setOpen(false);
                        setSearch("");
                      }}
                      data-testid={`calc-brand-option-${b.slug}`}
                      className="rc-option"
                    >
                      <span className="rc-brand-chip rc-brand-chip-sm" style={{ background: b.color }}>
                        <BrandIcon logoKey={b.logo_key} name={b.name} className="w-full h-full" />
                      </span>
                      <span className="rc-option-name">{b.name}</span>
                      <span className="rc-option-rate">{b.rate}%</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* balance */}
        <div className="rc-field-wrap">
          <span className="rc-dollar">$</span>
          <input
            type="number"
            min={selected?.min || 0}
            max={selected?.max || 9999}
            value={balance}
            onChange={(e) => setBalance(Number(e.target.value || 0))}
            data-testid="calc-balance-input"
            className="rc-balance"
          />
          <span className="rc-balance-label">card balance</span>
        </div>

        {/* toggles */}
        <div className="rc-toggles">
          <button
            type="button"
            onClick={() => setHasReceipt((v) => !v)}
            data-testid="calc-receipt-toggle"
            className={`rc-toggle ${hasReceipt ? "rc-toggle-on" : ""}`}
            style={hasReceipt ? { backgroundImage: GRADIENT } : undefined}
          >
            {hasReceipt && <Check className="w-3.5 h-3.5" />}
            {hasReceipt ? "With receipt" : "Add receipt"}
          </button>
          <button
            type="button"
            onClick={() => setPartial((v) => !v)}
            data-testid="calc-partial-toggle"
            className={`rc-toggle ${partial ? "rc-toggle-on" : ""}`}
            style={partial ? { backgroundImage: GRADIENT } : undefined}
          >
            {partial ? "Partial balance" : "Unused / full"}
          </button>
        </div>

        {/* payout */}
        <div className="rc-payout">
          <div className="rc-payout-meta">
            <div className="rc-payout-label">You receive</div>
            {selected && (
              <div className="rc-payout-rate">at {effectiveRate}% rate</div>
            )}
          </div>
          <motion.div
            key={payout}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="rc-payout-value"
            style={{ backgroundImage: GRADIENT }}
            data-testid="calc-payout"
          >
            ${payout}
          </motion.div>
          <button
            type="button"
            onClick={goSubmit}
            data-testid="calc-sell-now-btn"
            className="rc-cta"
            style={{ backgroundImage: GRADIENT }}
          >
            Sell this card <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {!compact && (
          <p className="rc-foot">
            Estimate applies to the verified balance · No hidden fees · Final payout confirmed after human verification
          </p>
        )}
      </div>

      <style>{`
        .rc-shell {
          position: relative;
          overflow: hidden;
          border-radius: 28px;
          padding: 28px;
          background: radial-gradient(135% 135% at 0% 0%, #161b33 0%, #0f1226 42%, #0b0d17 100%);
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 40px 90px -40px rgba(25,28,59,0.9), inset 0 1px 0 rgba(255,255,255,0.06);
        }
        .rc-compact { padding: 22px; border-radius: 24px; }
        .rc-glow { position: absolute; border-radius: 9999px; filter: blur(60px); opacity: .5; pointer-events: none; }
        .rc-glow-1 { width: 240px; height: 240px; top: -90px; right: -70px; background: #346af8; }
        .rc-glow-2 { width: 220px; height: 220px; bottom: -100px; left: -60px; background: #2554d6; opacity: .35; }
        .rc-inner { position: relative; z-index: 2; display: flex; flex-direction: column; gap: 14px; }

        .rc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
        .rc-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: .22em; text-transform: uppercase; color: #8fadff; }
        .rc-title { font-family: "Hanken Grotesk", sans-serif; font-size: 22px; font-weight: 700; color: #ffffff; margin-top: 4px; letter-spacing: -.01em; }
        .rc-spark { width: 38px; height: 38px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 10px 22px -8px rgba(199,125,255,.7); flex-shrink: 0; }

        .rc-field-wrap { position: relative; }
        .rc-select {
          width: 100%; height: 54px; padding: 0 14px; border-radius: 14px;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
          display: flex; align-items: center; gap: 11px; cursor: pointer;
          transition: border-color .2s, background .2s;
        }
        .rc-select:hover { background: rgba(255,255,255,0.09); border-color: rgba(185,168,255,0.5); }
        .rc-brand-chip { width: 30px; height: 30px; border-radius: 9px; overflow: hidden; flex-shrink: 0; display: inline-flex; }
        .rc-brand-chip-sm { width: 26px; height: 26px; border-radius: 8px; }
        .rc-brand-name { font-weight: 600; color: #fff; font-size: 15px; }
        .rc-placeholder { color: rgba(255,255,255,0.5); font-size: 14px; }
        .rc-rate-pill { margin-left: auto; font-family: "DM Mono", monospace; font-weight: 700; font-size: 13px; color: #fff; padding: 4px 11px; border-radius: 9999px; }
        .rc-chevron { color: rgba(255,255,255,0.55); transition: transform .25s; flex-shrink: 0; }
        .rc-rate-pill + .rc-chevron { margin-left: 10px; }
        .rc-chevron-open { transform: rotate(180deg); }

        .rc-dropdown {
          position: absolute; z-index: 30; top: calc(100% + 8px); left: 0; width: 100%;
          max-height: 300px; overflow-y: auto; border-radius: 16px;
          background: #141830; border: 1px solid rgba(255,255,255,0.12);
          box-shadow: 0 30px 60px -24px rgba(0,0,0,.7);
        }
        .rc-search-wrap { position: sticky; top: 0; padding: 10px; background: #141830; border-bottom: 1px solid rgba(255,255,255,0.08); }
        .rc-search-icon { position: absolute; left: 21px; top: 50%; transform: translateY(-50%); color: rgba(255,255,255,0.4); }
        .rc-search-input { width: 100%; height: 38px; padding: 0 12px 0 36px; border-radius: 10px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #fff; font-size: 14px; outline: none; }
        .rc-search-input::placeholder { color: rgba(255,255,255,0.4); }
        .rc-search-input:focus { border-color: #8fadff; }
        .rc-options { padding: 6px; }
        .rc-option { width: 100%; padding: 9px 10px; border-radius: 10px; display: flex; align-items: center; gap: 11px; text-align: left; transition: background .15s; }
        .rc-option:hover { background: rgba(255,255,255,0.08); }
        .rc-option-name { font-size: 14px; color: #eceaf6; }
        .rc-option-rate { margin-left: auto; font-family: "DM Mono", monospace; font-size: 12px; color: #8fadff; font-weight: 600; }

        .rc-dollar { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: rgba(255,255,255,0.55); font-family: "DM Mono", monospace; font-size: 18px; }
        .rc-balance {
          width: 100%; height: 54px; padding: 0 110px 0 32px; border-radius: 14px;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
          color: #fff; font-family: "DM Mono", monospace; font-size: 18px; font-weight: 600; outline: none;
          transition: border-color .2s, background .2s;
        }
        .rc-balance:focus { border-color: #8fadff; background: rgba(255,255,255,0.09); }
        .rc-balance-label { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); font-size: 12px; color: rgba(255,255,255,0.4); pointer-events: none; }

        .rc-toggles { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .rc-toggle {
          height: 46px; border-radius: 13px; font-size: 13px; font-weight: 600;
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.75); transition: all .2s;
        }
        .rc-toggle:hover { border-color: rgba(185,168,255,0.5); color: #fff; }
        .rc-toggle-on { color: #fff; border-color: transparent; box-shadow: 0 12px 26px -12px rgba(199,125,255,.7); }

        .rc-payout {
          margin-top: 4px; border-radius: 18px; padding: 18px;
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          display: flex; flex-direction: column; gap: 12px;
        }
        .rc-payout-meta { display: flex; align-items: baseline; justify-content: space-between; }
        .rc-payout-label { font-size: 11px; text-transform: uppercase; letter-spacing: .18em; font-weight: 700; color: rgba(255,255,255,0.5); }
        .rc-payout-rate { font-size: 12px; color: #8fadff; font-weight: 600; }
        .rc-payout-value {
          font-family: "Hanken Grotesk", sans-serif; font-weight: 800; font-size: 46px; line-height: 1;
          letter-spacing: -.02em;
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }
        .rc-compact .rc-payout-value { font-size: 38px; }
        .rc-cta {
          height: 52px; border-radius: 9999px; color: #fff; font-weight: 700; font-size: 15px;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          box-shadow: 0 16px 34px -14px rgba(255,95,162,.75); transition: transform .18s, box-shadow .18s;
        }
        .rc-cta:hover { transform: translateY(-2px); box-shadow: 0 22px 40px -14px rgba(255,95,162,.85); }
        .rc-foot { font-size: 12px; color: rgba(255,255,255,0.45); text-align: center; }
      `}</style>
    </div>
  );
}
